import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, Injector, OnDestroy, inject, signal, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ApiTesterComponent } from './api-tester.component';
import { JsonFormatterComponent } from './json-formatter.component';
import { JwtDecoderComponent } from './jwt-decoder.component';
import { RegexTesterComponent } from './regex-tester.component';
import { SqlFormatterComponent } from './sql-formatter.component';
import TOOLS from './tools.json';

type ToolId = 'json' | 'jwt' | 'sql' | 'regex' | 'api';

interface Tool {
  id: ToolId;
  slug: string;
  title: string;
  tag: string;
  description: string;
  pageTitle: string;
}

// Each tool also has a static landing page at /<slug>/ (generated by scripts/tool-pages.mjs).
const TOOL_PATH = /^\/([\w-]+)\/?(?:index\.html)?$/;

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select, textarea, a[href], [tabindex]:not([tabindex="-1"])';

@Component({
  selector: 'app-toolbox',
  standalone: true,
  // Each tool is only referenced inside a @defer block, so it is split into its own lazy chunk.
  imports: [NgTemplateOutlet, JsonFormatterComponent, JwtDecoderComponent, SqlFormatterComponent, RegexTesterComponent, ApiTesterComponent],
  templateUrl: './toolbox.component.html',
  styleUrls: ['./toolbox.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToolboxComponent implements OnDestroy {
  readonly tools = TOOLS as Tool[];

  readonly active = signal<Tool | null>(null);
  readonly isOpen = signal(false);
  readonly isFullscreen = signal(false);

  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');
  private readonly injector = inject(Injector);
  private returnFocus: HTMLElement | null = null;
  private unmountTimer?: ReturnType<typeof setTimeout>;
  private ownsHistoryEntry = false;
  private titleBeforeOpen = '';

  constructor() {
    const slug = TOOL_PATH.exec(location.pathname)?.[1];
    const tool = this.tools.find(t => t.slug === slug);
    if (tool) this.show(tool, null, true);
    window.addEventListener('popstate', this.onPopState);
  }

  // Opening from a card gives the tool its own URL, so it can be bookmarked or shared.
  open(tool: Tool, trigger: HTMLElement): void {
    this.show(tool, trigger, false);
    history.pushState({ toolSlug: tool.slug }, '', `/${tool.slug}/`);
    this.ownsHistoryEntry = true;
  }

  close(): void {
    if (!this.isOpen()) return;
    if (this.ownsHistoryEntry) {
      history.back(); // onPopState hides the modal and restores the previous URL
    } else {
      this.hide();
    }
  }

  // The tool's URL is in the address bar, so a reload reopens the same tool with fresh code.
  reloadPage(): void {
    location.reload();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.unmountTimer);
    window.removeEventListener('popstate', this.onPopState);
    document.documentElement.style.removeProperty('overflow');
  }

  private readonly onPopState = ({ state }: PopStateEvent) => {
    const tool = this.tools.find(t => t.slug === state?.toolSlug);
    if (tool) {
      this.show(tool, null, this.isFullscreen());
      this.ownsHistoryEntry = true;
    } else if (this.isOpen()) {
      this.hide();
    }
  };

  private show(tool: Tool, trigger: HTMLElement | null, fullscreen: boolean): void {
    clearTimeout(this.unmountTimer);
    if (!this.isOpen()) {
      this.returnFocus = trigger;
      this.titleBeforeOpen = document.title;
    }
    this.active.set(tool);
    this.isFullscreen.set(fullscreen);
    this.isOpen.set(true);
    document.title = tool.pageTitle;
    document.documentElement.style.overflow = 'hidden';
    afterNextRender(() => this.panel().nativeElement.focus(), { injector: this.injector });
  }

  private hide(): void {
    this.ownsHistoryEntry = false;
    this.isOpen.set(false);
    document.title = this.titleBeforeOpen;
    document.documentElement.style.removeProperty('overflow');
    this.returnFocus?.focus({ preventScroll: true });
    // Unmount after the exit transition so tool state (e.g. a pasted token) is discarded.
    this.unmountTimer = setTimeout(() => {
      this.active.set(null);
      this.isFullscreen.set(false);
    }, 300);
  }

  private trapFocus(event: KeyboardEvent): void {
    const panel = this.panel().nativeElement;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement;
    if (event.shiftKey && (current === first || current === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
